/**
 * 분양가 지표 정본 — CPL A-3 (2026-09-23).
 *
 * **이 파일이 분양가 평당가를 계산하는 유일한 자리다.** 화면·크론·API 가 각자 적던
 * `amount / (area / 3.3058)` 을 여기 한 벌로 모은다.
 *
 * ── 정본 정의 ───────────────────────────────────────────────────────────────
 *  · 3.3㎡당 분양가 = 타입별 `lttot_avg_amount` ÷ (**공급면적** / 3.3058) 의 **세대수 가중평균**.
 *  · `lttot_avg_amount` 가 하나라도 없으면 그 현장은 **「최고가 기준」으로만** 표기한다
 *    (avg 와 top 을 섞어 평균내면 그 값은 둘 중 어느 것도 아니다).
 *  · 이름·주택형에 「임대」가 있으면 **분양가 비교에서 배제** 한다(삭제가 아니라 배제다).
 *
 * ── 실측이 바로잡은 것(2026-09-23 · apt_subscriptions 2,884행 / 주택형 14,214행) ──
 *  ① 종전 상류는 `lttot_top_amount` 로 계산한 값을 `price_per_pyeong_avg` 에 넣었다.
 *     「평균」이라 적힌 칸에 최고가가 들어 있었다.
 *  ② 종전 상류는 `reduce(...)/length` — **타입 산술평균** 이었다. 11세대짜리 타입과
 *     154세대짜리 타입이 같은 무게였다.
 *  ③ 종전 상류는 `parseFloat(type)` = **전용면적** 으로 나눴다. 화면 라벨은 평당가였고
 *     사람은 그것을 공급면적 평당가로 읽는다. 실측상 `area`(공급면적, 100% 채움)는
 *     `type`(전용면적)보다 약 35% 크다 — 즉 종전 값은 그만큼 부풀어 있었다.
 *
 * ⛔ 반올림 위치를 옮기지 말 것 — 자리수가 달라지면 표의 숫자가 바뀐다.
 */

/** 평↔㎡ 환산 상수. 1평 = 400/121㎡. ⛔ 이 숫자를 다른 파일에 다시 적지 말 것. */
export const PYEONG_SQM = 3.3058;

/**
 * 행 단위 평당가(만원). 넘기는 면적이 무엇인지는 **부르는 쪽이 책임진다** —
 * 분양가는 공급면적, 실거래는 전용면적이다(실거래 원장에 공급면적이 없다).
 * ⚠️ 면적이 10㎡ 이하면 0 을 낸다(종전 가드 그대로). 표시부가 `> 0` 으로 거른다.
 */
export function pyeongPrice(amountManwon: number, areaSqm: number): number {
  return amountManwon > 0 && areaSqm > 10
    ? Math.round(amountManwon / (areaSqm / PYEONG_SQM))
    : 0;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 1. 주택형 한 행 읽기
 * ──────────────────────────────────────────────────────────────────────────── */

/** `apt_subscriptions.house_type_info[]` 한 원소. 청약홈 원본 키를 그대로 쓴다. */
export type HouseTypeRow = {
  /** 주택형. `059.9958A` 꼴 — **정수부가 전용면적(㎡)** 이다. */
  type?: string | null;
  /** SUPLY_AR — **공급면적(㎡)**. 실측 14,214행 전부 숫자 문자열이다. */
  area?: string | number | null;
  /** SUPLY_HSHLDCO — 일반공급 세대수. */
  supply?: number | null;
  /** SPSPLY_HSHLDCO — 특별공급 세대수. */
  spsply_hshldco?: number | null;
  /** 층별 평균 분양가(만원). PDF 파싱이 채운다 — 실측 채움률 8,567/14,214 (60.3%). */
  lttot_avg_amount?: number | null;
  /** 최고층 분양가(만원). 청약홈이 준다 — 실측 채움률 13,317/14,214 (93.7%). */
  lttot_top_amount?: number | null;
  lttot_min_amount?: number | null;
};

/** 숫자로 읽되, 못 읽으면 0. `'80.6001'` 같은 문자열이 그대로 온다. */
function num(v: unknown): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * 공급면적(㎡). `area`(SUPLY_AR) 만 본다.
 * ⛔ 없으면 전용면적으로 «대신하지» 않는다 — 두 면적은 35% 다르고, 섞으면 같은 표 안에서
 *    행마다 다른 기준이 선다. 없는 행은 계산에서 빠진다.
 */
export function supplyAreaOf(t: HouseTypeRow): number {
  return num(t?.area);
}

/**
 * 전용면적(㎡). 주택형 문자열의 숫자부다(`059.9958A` → 59.9958).
 * ⚠️ `areaTypeLabel` 과 달리 **반올림하지 않는다** — 이쪽은 표시가 아니라 계산이다.
 */
export function exclusiveAreaOf(t: HouseTypeRow): number {
  return num(String(t?.type ?? '').replace(/[A-Za-z]/g, ''));
}

/** 세대수 = 일반공급 + 특별공급. 실측 2,567/2,791(92.0%)에서 `tot_supply_hshld_co` 와 정확히 일치한다. */
export function unitsOf(t: HouseTypeRow): number {
  const gen = Number(t?.supply ?? 0);
  const sp = Number(t?.spsply_hshldco ?? 0);
  const total = (Number.isFinite(gen) ? gen : 0) + (Number.isFinite(sp) ? sp : 0);
  return total > 0 ? total : 0;
}

/**
 * 임대인가 — 분양가 비교에서 **배제** 한다.
 *
 * ⚠️ 배제이지 삭제가 아니다. 임대 물량은 실재하고 공급 표·세대수에는 그대로 선다.
 *    분양가(사는 값)와 임대료(빌리는 값)를 같은 축에 놓지 않을 뿐이다.
 * ⚠️ 실측: 주택형 문자열에 「임대」가 든 행은 0건이고, 단지명(`house_nm`)에 든 현장이
 *    94곳이다. 그래서 **이름 쪽이 실질 관문** 이지만 둘 다 본다 — 원본 표기가 바뀔 수 있다.
 */
export function isRentalLabel(s: unknown): boolean {
  const v = String(s ?? '');
  return v.includes('임대') || v.includes('행복주택') || v.includes('국민임대') || v.includes('공공지원민간임대');
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2. 현장 단위 3.3㎡당 분양가 — **단일값**
 * ──────────────────────────────────────────────────────────────────────────── */

export type SupplyPriceBasis = 'min' | 'avg' | 'top';

export type SupplyPyeongPrice = {
  /** 만원 / 3.3㎡. 세대수 가중평균. */
  value: number;
  /** 어느 금액으로 쟀는가. `'top'` 이면 라벨이 「최고가 기준」을 달고 나가야 한다. */
  basis: SupplyPriceBasis;
  /** 화면에 그대로 내는 기준 문구. ⛔ 호출부가 따로 짓지 말 것. */
  label: string;
  /** 가중에 쓰인 세대수 합. 표본 크기다. */
  units: number;
  /** 채택된 주택형 수. */
  typeCount: number;
};

/** 기준 문구 정본. ⛔ 이 두 줄 밖에서 평당가 라벨을 짓지 말 것. */
export const SUPPLY_PYEONG_LABEL = '3.3㎡당 분양가(공급면적 기준)';
export const SUPPLY_PYEONG_LABEL_TOP = '3.3㎡당 분양가(공급면적 기준 · 최고가 기준)';
/** 2026-10-24 — 화면이 «최저가 기준» 을 고를 수 있을 때(모든 채택 행에 `lttot_min_amount`). */
export const SUPPLY_PYEONG_LABEL_MIN = '3.3㎡당 분양가(공급면적 기준 · 최저가 기준)';

/** `house_type_info` 안의 금액 칸. ⛔ 이 셋 말고 다른 칸을 분양가로 읽지 말 것. */
export type SupplyAmountField = 'lttot_min_amount' | 'lttot_avg_amount' | 'lttot_top_amount';

/* ⛔ A-3 이 두었던 `supplyPyeongPriceOf(types, field, siteName)` 는 **없앴다**(A-4 · 2026-09-23).
 *    «금액 칸 하나» 를 받는 시그니처 자체가 함정이었다 — 부르는 쪽이 field 마다 따로 부르게 되고,
 *    그러면 field 마다 채택 행 집합이 갈려 min > max 가 선다(실측 52곳). 세 값이 필요하면
 *    `supplyPyeongTriple()` 을, 한 값이면 `supplyPyeongPrice()` 를 쓴다.
 *    ⛔ 「한 칸만 필요해서」라는 이유로 이 함수를 되살리지 말 것. */

/**
 * 세 컬럼(min/avg/max)을 **한 집합에서 한 번에** 낸다 — CPL A-4 (2026-09-23).
 *
 * ⛔ 금액 칸마다 따로 가중평균하면 **채택 행 집합이 칸마다 달라진다**.
 *    `lttot_top_amount` 는 청약홈이 93.7% 채우고 `lttot_min/avg_amount` 는 PDF 파싱이 60.3%만
 *    채운다. 그래서 min 은 A·B 타입에서, max 는 A·B·C 타입에서 나오고, C 가 싼 타입이면
 *    **min > max 가 구조적으로 성립한다**. 실측 2026-09-23: 그렇게 역전된 현장이 **52곳**.
 * → 집합을 «한 번» 고정한다: 비임대 · 공급면적 > 10㎡ · 세대수 > 0 · **세 금액이 모두 있는 행**.
 *   그 집합에서 세 값을 각각 세대수 가중평균한다.
 *   ⛔ 가중 방식·면적 기준·임대 배제는 `supplyPyeongPrice()` 정본 그대로다 — 고친 것은
 *      «집합 선택» 하나뿐이다. 새 산식이 아니다.
 *
 * ── 실측이 말하는 것(2026-09-23 · 주택형 14,307행) ──
 *  · 세 금액이 모두 선 행 8,316 / 현장 1,874. 집합 고정 후 값이 서는 현장 **1,826**.
 *  · 집합 고정만으로 `min > max` 는 **52 → 0**.
 *  · 그러나 `avg > max` 는 **55곳 남는다** — 이것은 집합 문제가 아니라 **출처가 다른 탓**이다.
 *    min·avg 는 공고문 PDF 의 층별 표에서 오고 max(`lttot_top_amount`)는 청약홈이 준다.
 *    행 단위로 `lttot_avg_amount > lttot_top_amount` 인 주택형이 **399행** 있다.
 *    ⛔ 그 행을 «버리거나 값을 눌러» 순서를 만들지 않는다 — 그러면 출처 불일치가 숨는다.
 *       표시부가 `pyeongRangeSafe()` 로 막고, 남은 55곳은 평균 하나만 나간다.
 */
export type SupplyPyeongTriple = {
  /** 만원 / 3.3㎡ · 세대수 가중평균. 셋 다 «같은 주택형 집합» 에서 나왔다. */
  min: number;
  avg: number;
  max: number;
  /** 가중에 쓰인 세대수 합. */
  units: number;
  /** 채택된 주택형 수. */
  typeCount: number;
};

export function supplyPyeongTriple(
  types: unknown,
  siteName?: string | null,
): SupplyPyeongTriple | null {
  if (isRentalLabel(siteName)) return null;
  const rows = (Array.isArray(types) ? types : []) as HouseTypeRow[];

  /* 집합을 «여기서 한 번» 고정한다. 아래 세 합산이 모두 이 배열만 돈다. */
  const set = rows.filter(
    (t) =>
      t &&
      !isRentalLabel(t.type) &&
      supplyAreaOf(t) > 10 &&
      unitsOf(t) > 0 &&
      num(t.lttot_min_amount) > 0 &&
      num(t.lttot_avg_amount) > 0 &&
      num(t.lttot_top_amount) > 0,
  );
  if (set.length === 0) return null;

  let units = 0;
  const w: Record<SupplyAmountField, number> = {
    lttot_min_amount: 0,
    lttot_avg_amount: 0,
    lttot_top_amount: 0,
  };
  for (const t of set) {
    const u = unitsOf(t);
    const area = supplyAreaOf(t);
    units += u;
    w.lttot_min_amount += pyeongPrice(num(t.lttot_min_amount), area) * u;
    w.lttot_avg_amount += pyeongPrice(num(t.lttot_avg_amount), area) * u;
    w.lttot_top_amount += pyeongPrice(num(t.lttot_top_amount), area) * u;
  }
  if (units <= 0) return null;

  const min = Math.round(w.lttot_min_amount / units);
  const avg = Math.round(w.lttot_avg_amount / units);
  const max = Math.round(w.lttot_top_amount / units);
  if (min <= 0 || avg <= 0 || max <= 0) return null;

  return { min, avg, max, units, typeCount: set.length };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 2-b. 표시 판정 — 범위인가 한 값인가
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * 평당가를 **범위로 말해도 되는가** — 판정 정본 · CPL A-4 (2026-09-23).
 *
 * ⛔ 화면·크론 어디서도 `if (min > max)` 를 다시 적지 말 것. 같은 사실을 두 자리에서 재면
 *    한쪽만 고치는 날 두 화면이 서로 다른 말을 한다. 판정은 이 함수 하나다.
 *
 * ── 판정 규칙(Node 2026-09-23) ──
 *   **최저 ≤ 평균 ≤ 최고가 성립할 때만 범위** 를 쓴다. 역전 행만의 규칙이 아니라 **전체 규칙**이다.
 *   성립하지 않으면 **평균 하나** 만 말한다. 평균조차 없으면 **아무 말도 하지 않는다**(`null`).
 *
 * ⚠️ 평균이 없으면 범위도 내지 않는다 — min·max 만으로는 순서를 검증할 축이 없다.
 *    실측 2026-09-23: `_min` 이 있는데 `_avg` 가 없는 현장은 **0곳** 이라 손해가 없다.
 * ⚠️ 이 판정이 낳는 실물 차이(2026-09-23 apt_subscriptions):
 *    min·max 둘 다 있는 1,826곳 중 **1,722곳만 범위**, **104곳은 평균 하나**
 *    (min > max 52곳 + avg > max 나머지). 종전 화면은 그 104곳에도 범위를 냈고,
 *    그중 52곳은 「1,527만원에서 1,253만원 사이」처럼 **큰 수에서 작은 수로** 갔다.
 *
 * ── `label` 판정 근거 — DB위생 C1 (2026-09-25, Node 확정) ──
 *   `kind:'single'` 일 때 그 값을 「평균」이라 불러도 되는지 「최고가」라 불러야 하는지는
 *   **min·max 가 둘 다 있는가 없는가**로 가른다.
 *   ⛔ 이 판정을 이 함수 «밖에서» 다시 하지 말 것 — `AptSitePageView.tsx`·`site-overview.ts`·
 *      `blog-apt-v2/route.ts` 세 소비자가 전부 이 필드 하나를 쓴다(판정 한 곳 규율).
 *   · min·max 가 **둘 다 있는데** range 조건만 깨졌다(avg>max 55곳 등) → 그 avg 는
 *     `supplyPyeongTriple()` 이 낸 **진짜 세대수 가중평균**이다. label = `'평균'`.
 *   · min·max 가 **둘 다 없는데** avg 만 있다 → 정본 재계산(트리플) 이후로는 이 모양이
 *     `supplyPyeongPrice()` **단일값 폴백**에서만 생긴다(트리플이 서면 세 값이 항상 함께
 *     채워진다 — `supplyPyeongTriple()` 참조, 채택 집합이 하나라 셋 다 서거나 아무것도
 *     안 선다). 그 단일값 폴백은 실측상(2026-09-25, 705행 조사) **전량 `basis='top'`**
 *     이었다 — avg 가 «모든» 채택 행에 있어야 `basis='avg'` 가 되는데, 그 조건을 만족하는
 *     행이 0건이었다. 그래서 label = `'최고가'`.
 *   ⚠️ 이것은 **추론**이다(«단일값 폴백이면 top이다» 가 항상 참이라는 보장은 코드가 아니라
 *      실측이 준다) — 이 추론이 깨지는 유일한 길은 avg 단일값 폴백(`basis='avg'`)이 실제로
 *      발생하는 경우인데, 그러면 label 이 '최고가'로 잘못 나간다. 백필·재검토 때마다
 *      «min·max 둘 다 null·avg만 있는 행 수」와 「단일값 폴백 대상 행 수」가 일치하는지
 *      실측으로 다시 닫아야 한다(추정으로 넘기지 말 것).
 *   ⛔ **범위 문장 금지** — label='최고가' 인 단일값을 min~max 범위처럼 말하지 않는다.
 *      출처가 하나뿐이라 「최저~최고」라는 말 자체가 성립하지 않는다.
 */
export type PyeongRangeDisplay =
  | { kind: 'range'; min: number; avg: number; max: number; label: '평균' }
  | { kind: 'single'; avg: number; label: '평균' | '최고가' };

/** 양수 유한값만 값으로 친다. 0·NaN·음수·null 은 「없음」이다. */
function positive(v: number | null | undefined): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function pyeongRangeSafe(
  min: number | null | undefined,
  avg: number | null | undefined,
  max: number | null | undefined,
): PyeongRangeDisplay | null {
  const a = positive(avg);
  if (a === null) return null;
  const lo = positive(min);
  const hi = positive(max);
  if (lo !== null && hi !== null && lo <= a && a <= hi) {
    return { kind: 'range', min: lo, avg: a, max: hi, label: '평균' };
  }
  // 위 「label 판정 근거」 그대로: min·max 가 «둘 다» 없을 때만 최고가 단일값 폴백으로 읽는다.
  const label: '평균' | '최고가' = lo === null && hi === null ? '최고가' : '평균';
  return { kind: 'single', avg: a, label };
}

/**
 * 현장의 3.3㎡당 분양가 **하나**.
 *
 * 값이 서지 않으면 `null` 이다 — 0 이나 추정치를 내지 않는다.
 * 서지 않는 경우: 주택형이 없다 / 공급면적이 없다 / 금액이 없다 / 임대 현장이다.
 *
 * @param types `apt_subscriptions.house_type_info`
 * @param siteName 단지명. 「임대」가 들어 있으면 통째로 배제한다.
 */
export function supplyPyeongPrice(
  types: unknown,
  siteName?: string | null,
  opt?: { preferMin?: boolean },
): SupplyPyeongPrice | null {
  if (isRentalLabel(siteName)) return null;
  const rows = (Array.isArray(types) ? types : []) as HouseTypeRow[];
  if (rows.length === 0) return null;

  // 임대 주택형 배제 + 공급면적·세대수가 있는 행만.
  const usable = rows.filter(
    (t) => t && !isRentalLabel(t.type) && supplyAreaOf(t) > 10 && unitsOf(t) > 0,
  );
  if (usable.length === 0) return null;

  /* 기준 선택 — 현장 «전체» 에 하나다.
     ⛔ 타입마다 avg / top 을 골라 섞지 않는다. 섞은 평균은 평균가도 최고가도 아니다.
        avg 가 «모든» 채택 행에 있을 때만 'avg' 다. */
  /* 2026-10-24 Node 「분양가는 최고가보다 최저가로」 — 화면(`preferMin`)은 min → avg → top 순으로 고른다.
     ⛔ 크론(apt-crawl-pricing·apt-parse-pdf-pricing)은 `preferMin` 을 넘기지 않는다 — 그 값은
        `price_per_pyeong_avg` 컬럼에 저장되고, 거기에 최저가 기준 값을 넣으면 A-3 이 고친
        「_avg 칸에 평균이 아닌 값」 결함이 그대로 되살아난다.
     ⚠️ min 도 avg 와 같은 «전 행 채움» 규칙이다(섞지 않는다). 실측상 min 은 PDF 파싱이 채우는 칸이라
        대부분 현장에서는 서지 않고 종전 avg/top 으로 내려간다 — 없는 최저가를 지어내지 않는다. */
  const allHaveMin = !!opt?.preferMin && usable.every((t) => num(t.lttot_min_amount) > 0);
  const allHaveAvg = usable.every((t) => num(t.lttot_avg_amount) > 0);
  const basis: SupplyPriceBasis = allHaveMin ? 'min' : allHaveAvg ? 'avg' : 'top';
  const amountOf = (t: HouseTypeRow) =>
    basis === 'min' ? num(t.lttot_min_amount) : basis === 'avg' ? num(t.lttot_avg_amount) : num(t.lttot_top_amount);

  const priced = usable.filter((t) => amountOf(t) > 0);
  if (priced.length === 0) return null;

  // 타입별 평당가의 «세대수 가중평균».
  let weighted = 0;
  let units = 0;
  for (const t of priced) {
    const pp = pyeongPrice(amountOf(t), supplyAreaOf(t));
    if (pp <= 0) continue;
    const u = unitsOf(t);
    weighted += pp * u;
    units += u;
  }
  if (units <= 0) return null;

  const value = Math.round(weighted / units);
  if (value <= 0) return null;

  return {
    value,
    basis,
    label: basis === 'min' ? SUPPLY_PYEONG_LABEL_MIN : basis === 'avg' ? SUPPLY_PYEONG_LABEL : SUPPLY_PYEONG_LABEL_TOP,
    units,
    typeCount: priced.length,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * 3. 분양가 vs 실거래 — **같은 전용면적대 · 총액**
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * 전용면적대. 주택법 국민주택 규모(85㎡)를 축으로 한 통상 구간이다.
 * ⚠️ 실거래 원장(`apt_transactions`)에는 공급면적이 없다. 그래서 비교 축은 **전용면적** 이다 —
 *    분양가 평당가(공급면적 기준)와 축이 다르고, 그래서 여기서는 평당가가 아니라 **총액** 을 비교한다.
 */
const BAND_EDGES = [60, 85, 102, 135] as const;

export function areaBandOf(exclusiveSqm: number): number | null {
  if (!(exclusiveSqm > 0)) return null;
  for (let i = 0; i < BAND_EDGES.length; i++) {
    if (exclusiveSqm < BAND_EDGES[i]) return i;
  }
  return BAND_EDGES.length;
}

/** 구간 라벨 — **실제 경계값으로 짓는다**. ⛔ 하드코딩한 문구를 따로 두지 말 것. */
export function areaBandLabel(band: number): string {
  if (band <= 0) return `전용 ${BAND_EDGES[0]}㎡ 미만`;
  if (band >= BAND_EDGES.length) return `전용 ${BAND_EDGES[BAND_EDGES.length - 1]}㎡ 이상`;
  return `전용 ${BAND_EDGES[band - 1]}~${BAND_EDGES[band]}㎡`;
}

export type TradeRow = { deal_amount?: number | string | null; exclusive_area?: number | string | null };

export type SupplyVsTrade = {
  band: number;
  /** 「전용 60~85㎡」 — 실제 경계값 산출. */
  bandLabel: string;
  basis: SupplyPriceBasis;
  /** 분양가(만원) — 그 면적대 안에서. */
  supplyMin: number;
  supplyMax: number;
  /** 세대수 가중평균 분양가(만원). */
  supplyAvg: number;
  supplyUnits: number;
  /** 실거래(만원) — «같은» 면적대. */
  tradeMin: number;
  tradeMax: number;
  tradeAvg: number;
  tradeCount: number;
};

/**
 * 분양가와 실거래를 **같은 전용면적대에서 총액으로** 나란히 놓는다.
 *
 * ⛔ 「프리미엄 / 저평가」 같은 **판단** 을 내지 않는다 — 두 값을 같은 축에 놓는 것까지가
 *    우리가 할 수 있는 말이다(DS_RULES §2-3 · A2 가 칩을 걷은 이유).
 * ⛔ 면적대가 겹치지 않으면 `null` 이다. 종전 결함이 바로 그것이었다 — 59㎡ 분양가의
 *    산술 중간값을 84㎡ 실거래 평균과 비교하고 그 비율에 이름을 붙였다.
 *
 * 고르는 면적대: 공급 세대수가 가장 많은 면적대 중 실거래가 2건 이상인 곳.
 */
export function supplyVsTradeByBand(
  types: unknown,
  trades: unknown,
  siteName?: string | null,
): SupplyVsTrade | null {
  if (isRentalLabel(siteName)) return null;
  const rows = (Array.isArray(types) ? types : []) as HouseTypeRow[];
  const tr = (Array.isArray(trades) ? trades : []) as TradeRow[];
  if (rows.length === 0 || tr.length === 0) return null;

  const usable = rows.filter((t) => t && !isRentalLabel(t.type) && exclusiveAreaOf(t) > 10 && unitsOf(t) > 0);
  if (usable.length === 0) return null;

  const allHaveAvg = usable.every((t) => num(t.lttot_avg_amount) > 0);
  const basis: SupplyPriceBasis = allHaveAvg ? 'avg' : 'top';
  const amountOf = (t: HouseTypeRow) =>
    basis === 'avg' ? num(t.lttot_avg_amount) : num(t.lttot_top_amount);

  // 면적대별 공급 묶음.
  const byBand = new Map<number, HouseTypeRow[]>();
  for (const t of usable) {
    if (amountOf(t) <= 0) continue;
    const b = areaBandOf(exclusiveAreaOf(t));
    if (b === null) continue;
    const arr = byBand.get(b);
    if (arr) arr.push(t);
    else byBand.set(b, [t]);
  }
  if (byBand.size === 0) return null;

  // 면적대별 실거래 묶음.
  const tradeByBand = new Map<number, number[]>();
  for (const t of tr) {
    const amt = num(t?.deal_amount);
    const ea = num(t?.exclusive_area);
    const b = areaBandOf(ea);
    if (amt <= 0 || b === null) continue;
    const arr = tradeByBand.get(b);
    if (arr) arr.push(amt);
    else tradeByBand.set(b, [amt]);
  }
  if (tradeByBand.size === 0) return null;

  // 겹치는 면적대 중 공급 세대수가 가장 많은 곳. 실거래는 2건 이상이어야 «범위» 가 선다.
  let best: { band: number; units: number } | null = null;
  for (const [b, ts] of byBand) {
    const amounts = tradeByBand.get(b);
    if (!amounts || amounts.length < 2) continue;
    const units = ts.reduce((s, t) => s + unitsOf(t), 0);
    if (!best || units > best.units) best = { band: b, units };
  }
  if (!best) return null;

  const supplyTypes = byBand.get(best.band) as HouseTypeRow[];
  const supplyAmounts = supplyTypes.map(amountOf);
  let wsum = 0;
  let units = 0;
  for (const t of supplyTypes) {
    const u = unitsOf(t);
    wsum += amountOf(t) * u;
    units += u;
  }
  const amounts = tradeByBand.get(best.band) as number[];

  return {
    band: best.band,
    bandLabel: areaBandLabel(best.band),
    basis,
    supplyMin: Math.min(...supplyAmounts),
    supplyMax: Math.max(...supplyAmounts),
    supplyAvg: units > 0 ? Math.round(wsum / units) : 0,
    supplyUnits: units,
    tradeMin: Math.min(...amounts),
    tradeMax: Math.max(...amounts),
    tradeAvg: Math.round(amounts.reduce((s, a) => s + a, 0) / amounts.length),
    tradeCount: amounts.length,
  };
}
